import React, { useEffect, useMemo, useState } from "react";

const API_PATH = "/api/art-day-brief-v2";

const BRAND = {
  turquoise: "#00B4A6",
  turquoiseLight: "#5FF5EA",
  turquoiseDark: "#00796B",
  turquoiseMist: "#DDFBF8",
  gold: "#D4AF37",
  goldLight: "#FFE08A",
  ink: "#071313",
  muted: "#637D7A",
  cream: "#FFF9EC",
  deep: "#031817",
};

function normaliseLang(browserLang) {
  const raw = (browserLang || "en").toLowerCase();
  return raw.split("-")[0] || "en";
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeString(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function buildUrl(params) {
  const usp = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    usp.set(key, String(value));
  });

  return `${API_PATH}?${usp.toString()}`;
}

function forceHttpsUrl(url) {
  if (!url) return "";

  const clean = String(url).trim();

  if (clean.startsWith("http://")) {
    return `https://${clean.slice("http://".length)}`;
  }

  return clean;
}

function formatDate(value) {
  if (!value) return "";

  try {
    return new Date(value).toLocaleString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function itemImage(item) {
  return forceHttpsUrl(
    item?.image_url ||
      item?.image ||
      item?.thumbnail ||
      item?.thumbnail_url ||
      ""
  );
}

function compactText(value, max = 180) {
  const clean = String(value || "").replace(/\s+/g, " ").trim();

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max).trim()}…`;
}

function setMetaTag(name, content) {
  if (!content) return;

  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function setPropertyMeta(property, content) {
  if (!content) return;

  let element = document.querySelector(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

function setJsonLd(id, payload) {
  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

function scoreLabel(score) {
  const value = Number(score || 0);

  if (value >= 260) return "Do not miss";
  if (value >= 210) return "Top pick";
  if (value >= 160) return "Strong lead";
  if (value >= 110) return "Worth opening";
  return "Local find";
}

function sourceBadge(source) {
  const clean = String(source || "").toLowerCase();

  if (clean.includes("ticketmaster")) return "Ticketed";
  if (clean.includes("official")) return "Official";
  if (clean.includes("openstreetmap")) return "Place";
  if (clean.includes("search")) return "Search lead";
  if (clean.includes("eventbrite")) return "Event";
  if (clean.includes("google")) return "Map lead";

  return "Curated";
}

function classifyItem(item) {
  const text = [
    item?.title,
    item?.venue,
    item?.description,
    item?.curator_note,
    item?.category,
    item?.source,
    safeArray(item?.audience).join(" "),
    safeArray(item?.reasons).join(" "),
  ]
    .join(" ")
    .toLowerCase();

  const has = (...words) => words.some((word) => text.includes(word));

  if (
    has(
      "art fair",
      "art market",
      "craft market",
      "makers market",
      "artist market",
      "print fair",
      "affordable art fair",
      "collectors fair",
      "auction",
      "sale"
    )
  ) {
    return "markets";
  }

  if (
    has(
      "temporary exhibition",
      "exhibition",
      "show",
      "solo show",
      "group show",
      "installation",
      "private view",
      "preview",
      "opening"
    )
  ) {
    return "exhibitions";
  }

  if (
    has(
      "workshop",
      "life drawing",
      "drawing class",
      "painting class",
      "paint and sip",
      "artist talk",
      "talk",
      "lecture",
      "masterclass",
      "open studio",
      "studio visit"
    )
  ) {
    return "workshops";
  }

  if (has("gallery", "contemporary art", "art space")) {
    return "galleries";
  }

  if (has("museum", "collection", "historic house")) {
    return "museums";
  }

  return "best";
}

function enrichItem(item, fallbackType = "best") {
  const type = classifyItem(item) || fallbackType;

  const labels = {
    best: "Best pick",
    exhibitions: "Exhibition",
    markets: "Art market",
    workshops: "Workshop",
    galleries: "Gallery",
    museums: "Museum",
  };

  const actionLabels = {
    best: "Open lead",
    exhibitions: "View exhibition",
    markets: "Explore market",
    workshops: "Join activity",
    galleries: "Open gallery",
    museums: "Open museum",
  };

  return {
    ...item,
    radar_type: type,
    radar_label: labels[type] || "Art lead",
    action_label: actionLabels[type] || "Open listing",
  };
}

function uniqueItems(items) {
  const seen = new Set();

  return items.filter((item) => {
    const key = [
      item?.id,
      item?.title,
      item?.venue,
      item?.url,
      item?.start,
    ]
      .filter(Boolean)
      .join("|")
      .toLowerCase();

    if (!key) return true;
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

function byScore(a, b) {
  return Number(b?.score || 0) - Number(a?.score || 0);
}

function getGoogleMapsUrl(item) {
  const query = [item?.title, item?.venue].filter(Boolean).join(" ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
}

function getReadableType(type) {
  const map = {
    best: "Best picks",
    exhibitions: "Exhibitions",
    markets: "Art markets",
    workshops: "Workshops",
    galleries: "Galleries",
    museums: "Museums",
  };

  return map[type] || "Art leads";
}

function CardImage({ item, feature = false }) {
  const img = itemImage(item);

  if (img) {
    return (
      <div className={`ard-image ${feature ? "ard-image--feature" : ""}`}>
        <img src={img} alt={item.title || "Art listing"} loading="lazy" />
        <div className="ard-imageShade" />

        <div className="ard-imageBadges">
          <span>{item.radar_label || sourceBadge(item.source)}</span>
          {item.image_source ? <span>{item.image_source}</span> : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`ard-image ard-image--fallback ${
        feature ? "ard-image--feature" : ""
      }`}
    >
      <div className="ard-fallbackOrb ard-fallbackOrb--one" />
      <div className="ard-fallbackOrb ard-fallbackOrb--two" />
      <span>{item?.radar_label || "Visual art"}</span>
    </div>
  );
}

function ArtCard({ item, feature = false }) {
  const date = formatDate(item.start);
  const audience = safeArray(item.audience);
  const reasons = safeArray(item.reasons);

  return (
    <article className={`ard-card ${feature ? "ard-card--feature" : ""}`}>
      <CardImage item={item} feature={feature} />

      <div className="ard-cardBody">
        <div className="ard-cardMeta">
          <span>{sourceBadge(item.source)}</span>

          {item.radar_label ? <span>{item.radar_label}</span> : null}

          {item.distance_km !== null && item.distance_km !== undefined ? (
            <span>{item.distance_km} km</span>
          ) : null}

          {date ? <span>{date}</span> : null}

          {item.score ? <span>{scoreLabel(item.score)}</span> : null}
        </div>

        <h3>{item.title}</h3>

        {item.venue && item.venue !== item.title ? (
          <div className="ard-venue">{item.venue}</div>
        ) : null}

        <p>
          {compactText(
            item.description ||
              item.curator_note ||
              "A useful visual art lead for painters, collectors and room-inspired art lovers.",
            feature ? 285 : 190
          )}
        </p>

        {item.curator_note ? (
          <div className="ard-curatorNote">{item.curator_note}</div>
        ) : null}

        <div className="ard-tags">
          {audience.slice(0, 3).map((tag) => (
            <span key={tag} className="ard-tag ard-tag--gold">
              {tag}
            </span>
          ))}

          {reasons.slice(0, 3).map((reason) => (
            <span key={reason} className="ard-tag">
              {reason}
            </span>
          ))}
        </div>

        <div className="ard-actions">
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="ard-openBtn"
            >
              {item.action_label || "Open listing"}
            </a>
          ) : null}

          {item.lat || item.lon || item.venue ? (
            <a
              href={getGoogleMapsUrl(item)}
              target="_blank"
              rel="noreferrer"
              className="ard-mapBtn"
            >
              Map
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function Section({
  eyebrow,
  title,
  text,
  items,
  emptyText,
  featureFirst = false,
}) {
  return (
    <section className="ard-section">
      <div className="ard-sectionHead">
        <div>
          <div className="ard-eyebrow">{eyebrow}</div>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>

        <div className="ard-count">{items.length}</div>
      </div>

      {items.length ? (
        <div className={featureFirst ? "ard-grid ard-grid--feature" : "ard-grid"}>
          {items.map((item, index) => (
            <ArtCard
              key={item.id || `${item.title}-${item.venue}-${index}`}
              item={item}
              feature={featureFirst && index === 0}
            />
          ))}
        </div>
      ) : (
        <div className="ard-empty">{emptyText}</div>
      )}
    </section>
  );
}

function LoadingPanel() {
  return (
    <section className="ard-loading">
      <div className="ard-loadingOrb" />

      <div>
        <div className="ard-eyebrow">Curating the art map</div>
        <h2>Finding exhibitions, markets, workshops and painting leads…</h2>
        <p>
          Checking local art places, museums, galleries, official pages,
          ticketed events and visual art listings.
        </p>
      </div>
    </section>
  );
}

function EmptyStartPanel() {
  return (
    <div className="ard-empty ard-empty--start">
      <h2>Start with a place</h2>
      <p>
        Type a city or use your location to build a beautiful local Art Radar
        for painters, painting buyers, collectors, gallery visitors and curious
        weekend wanderers.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="ard-footer">
      <div className="ard-footerInner">
        <div className="ard-footerBrand">
          <div className="ard-footerLogo">AF</div>

          <div>
            <h3>Art For My Room</h3>
            <p>
              Art activities nearby, original paintings worldwide, and rooms
              waiting for their destined artwork.
            </p>
          </div>
        </div>

        <div className="ard-footerCols">
          <div>
            <h4>Explore</h4>
            <a href="/">Home</a>
            <a href="/artist-room-matcher">Find Painting</a>
            <a href="/artist-launch">Upload Painting</a>
            <a href="/creator-radar">Creator Radar</a>
          </div>

          <div>
            <h4>Art Radar finds</h4>
            <span>Museums</span>
            <span>Temporary exhibitions</span>
            <span>Art markets</span>
            <span>Workshops and talks</span>
          </div>

          <div>
            <h4>Contact</h4>
            <a href="mailto:info@artformyroom.com">info@artformyroom.com</a>
            <span>UK-built art discovery</span>
          </div>
        </div>
      </div>

      <div className="ard-footerBottom">
        <span>© {new Date().getFullYear()} Art For My Room</span>
        <span>Useful art nearby. Paintings for real rooms.</span>
      </div>
    </footer>
  );
}

export default function StartYourDayPage() {
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [city, setCity] = useState("");
  const [activeView, setActiveView] = useState("best");
  const [query, setQuery] = useState("");

  const lang = useMemo(() => {
    const browserLang =
      typeof navigator !== "undefined" ? navigator.language : "en";

    return normaliseLang(browserLang);
  }, []);

  async function fetchArtBrief(params) {
    setIsLoading(true);
    setError("");

    try {
      const url = buildUrl({
        lang,
        focus:
          "visual-art,museums,galleries,temporary-exhibitions,art-markets,art-fairs,painting,paintings,painters,artist-events,open-studios,private-views,workshops,life-drawing,artist-talks,collectors",
        categories:
          "art,museum,gallery,exhibition,temporary-exhibition,painting,visual-arts,art-market,art-fair,artists-market,makers-market,print-fair,auction,private-view,opening,open-studio,studio-visit,workshop,artist-talk,life-drawing,drawing-class,painting-class,collectors,art-buyers",
        include:
          "painting,visual art,temporary exhibition,art market,art fair,gallery opening,private view,open studio,artist talk,life drawing,workshop,print fair,collectors,art buyers",
        exclude:
          "sport,comedy,nightclub,gambling,generic-music,theatre-only,football,casino,club-night",
        ...params,
      });

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
        },
      });

      const text = await response.text();
      let data = {};

      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text };
      }

      if (!response.ok) {
        throw new Error(`API error (${response.status}): ${text.slice(0, 260)}`);
      }

      setPayload(data);

      try {
        localStorage.setItem("artDay:lastPayload", JSON.stringify(data));
        localStorage.setItem("artDay:lastPayloadAt", new Date().toISOString());
      } catch {
        // localStorage can fail in private browsing
      }
    } catch (err) {
      setError(err.message || "Could not load the local art radar.");
    } finally {
      setIsLoading(false);
    }
  }

  function useMyLocation() {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported in this browser.");
      return;
    }

    setIsLoading(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        fetchArtBrief({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      (err) => {
        setIsLoading(false);
        setError(err.message || "Could not get location.");
      },
      {
        enableHighAccuracy: false,
        timeout: 9000,
        maximumAge: 5 * 60 * 1000,
      }
    );
  }

  function submitCity(event) {
    event.preventDefault();

    if (!city.trim()) return;

    fetchArtBrief({
      place: city.trim(),
    });
  }

  async function sharePage() {
    const loc = payload?.location?.display_name || "your area";
    const text = `Art Radar for ${loc}: exhibitions, art markets, galleries, museums, workshops and painting-friendly events.`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Art For My Room Art Radar",
          text,
          url: window.location.href,
        });
      } catch {
        // user cancelled
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(window.location.href);
    }
  }

  useEffect(() => {
    try {
      const cached = localStorage.getItem("artDay:lastPayload");

      if (cached) {
        setPayload(JSON.parse(cached));
      }
    } catch {
      // ignore localStorage errors
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const place = params.get("place");

    if (place) {
      setCity(place);
      fetchArtBrief({ place });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locName = payload?.location?.display_name || "your area";
  const widgets = payload?.widgets || {};

  const rawMuseums = safeArray(widgets?.museums?.items);
  const rawGalleries = safeArray(widgets?.galleries?.items);
  const rawArtEvents = safeArray(widgets?.art_events?.items);
  const rawEditorPicks = safeArray(widgets?.editor_picks?.items);
  const rawMarkets = safeArray(widgets?.art_markets?.items);
  const rawTemporaryExhibitions = safeArray(
    widgets?.temporary_exhibitions?.items || widgets?.exhibitions?.items
  );
  const rawWorkshops = safeArray(
    widgets?.workshops?.items || widgets?.artist_activities?.items
  );

  const counts = payload?.meta?.raw_counts || {};

  const allDiscoveredItems = useMemo(() => {
    return uniqueItems(
      [
        ...rawEditorPicks.map((item) => enrichItem(item, "best")),
        ...rawMuseums.map((item) => enrichItem(item, "museums")),
        ...rawGalleries.map((item) => enrichItem(item, "galleries")),
        ...rawArtEvents.map((item) => enrichItem(item, "best")),
        ...rawMarkets.map((item) => enrichItem(item, "markets")),
        ...rawTemporaryExhibitions.map((item) =>
          enrichItem(item, "exhibitions")
        ),
        ...rawWorkshops.map((item) => enrichItem(item, "workshops")),
      ].filter(Boolean)
    ).sort(byScore);
  }, [
    rawEditorPicks,
    rawMuseums,
    rawGalleries,
    rawArtEvents,
    rawMarkets,
    rawTemporaryExhibitions,
    rawWorkshops,
  ]);

  const grouped = useMemo(() => {
    const base = {
      best: [],
      exhibitions: [],
      markets: [],
      workshops: [],
      galleries: [],
      museums: [],
    };

    allDiscoveredItems.forEach((item) => {
      const type = item.radar_type || classifyItem(item);

      if (base[type]) {
        base[type].push(item);
      }

      if (item.score >= 150 || base.best.length < 12) {
        base.best.push(item);
      }
    });

    Object.keys(base).forEach((key) => {
      base[key] = uniqueItems(base[key]).sort(byScore);
    });

    return base;
  }, [allDiscoveredItems]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = grouped[activeView] || grouped.best || [];

    if (!q) return base;

    return base.filter((item) => {
      const text = [
        item.title,
        item.venue,
        item.description,
        item.curator_note,
        item.category,
        item.source,
        item.radar_label,
        safeArray(item.audience).join(" "),
        safeArray(item.reasons).join(" "),
      ]
        .join(" ")
        .toLowerCase();

      return text.includes(q);
    });
  }, [query, activeView, grouped]);

  const jsonLdItems = useMemo(() => {
    return allDiscoveredItems.slice(0, 22).map((item, index) => ({
      "@type": item.start ? "Event" : "Place",
      position: index + 1,
      name: item.title,
      description: item.description || item.curator_note,
      url: item.url,
      image: item.image_url || undefined,
      startDate: item.start || undefined,
      endDate: item.end || undefined,
      location: item.venue
        ? {
            "@type": "Place",
            name: item.venue,
          }
        : undefined,
    }));
  }, [allDiscoveredItems]);

  useEffect(() => {
    const canonical = `${window.location.origin}/startyourday`;
    const title = `Art Radar | Exhibitions, art markets and painting activities near ${locName}`;
    const description = `Find the best art activities near ${locName}: temporary exhibitions, art markets, art fairs, gallery openings, museums, workshops, artist talks, life drawing and painting-friendly events.`;

    document.title = title;

    setCanonical(canonical);
    setMetaTag("description", description);
    setMetaTag(
      "keywords",
      `art events near me, art markets near me, temporary exhibitions, painting exhibitions, galleries near me, museums near me, art fairs, private views, open studios, painting workshops, artist talks, life drawing, art buyers, painters, ${locName}`
    );
    setMetaTag("robots", "index, follow, max-image-preview:large");
    setMetaTag("theme-color", BRAND.turquoise);

    setPropertyMeta("og:title", title);
    setPropertyMeta("og:description", description);
    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:url", canonical);

    setJsonLd("art-radar-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      isPartOf: {
        "@type": "WebSite",
        name: "Art For My Room",
        url: window.location.origin,
      },
      about: [
        "visual art",
        "painters",
        "painting buyers",
        "temporary exhibitions",
        "art markets",
        "art fairs",
        "gallery openings",
        "open studios",
        "museums",
        "painting workshops",
      ],
      mainEntity: {
        "@type": "ItemList",
        name: `Art Radar near ${locName}`,
        itemListElement: jsonLdItems,
      },
    });
  }, [payload, locName, jsonLdItems]);

  const heroTitle =
    payload?.hero?.title ||
    "Find the best art activities around you";

  const heroSubtitle =
    payload?.hero?.subtitle ||
    "Art Radar discovers exhibitions, art markets, gallery openings, museums, workshops, private views, open studios and painting-friendly events near you, in one efficient place.";

  const heroEditorNote =
    payload?.hero?.editor_note ||
    "Built for painters, collectors and art lovers who want the useful art scene without opening twenty tabs. The radar prioritises visual art, painting relevance, distance, images, official links and practical discovery.";

  const painterPrompt =
    payload?.hero?.painter_prompt ||
    "For painters: use this radar to study how art is shown, priced, described and discovered. For buyers: use it to find the kind of artwork that could eventually belong in your room.";

  return (
    <main className="ard-page">
      <style>{`
        .ard-page {
          min-height: 100vh;
          color: ${BRAND.ink};
          background:
            radial-gradient(circle at 10% 8%, rgba(0, 180, 166, .24), transparent 28%),
            radial-gradient(circle at 88% 10%, rgba(212, 175, 55, .20), transparent 25%),
            radial-gradient(circle at 50% 92%, rgba(95, 245, 234, .16), transparent 30%),
            linear-gradient(180deg, #f9fffe 0%, #ecfffd 42%, #fff9ec 100%);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          overflow-x: hidden;
          position: relative;
        }

        .ard-page::before {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          opacity: .28;
          background-image:
            linear-gradient(rgba(0,180,166,.16) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,180,166,.16) 1px, transparent 1px);
          background-size: 38px 38px;
          animation: ardGridPulse 7s ease-in-out infinite;
        }

        .ard-page::after {
          content: "";
          position: fixed;
          width: 40vw;
          height: 40vw;
          right: -18vw;
          top: 8vh;
          border-radius: 999px;
          background: radial-gradient(circle, rgba(212,175,55,.15), transparent 65%);
          pointer-events: none;
          animation: ardFloat 9s ease-in-out infinite;
        }

        .ard-shell {
          position: relative;
          z-index: 2;
          width: min(1240px, calc(100% - 28px));
          margin: 0 auto;
          padding: 24px 0 84px;
        }

        .ard-hero {
          position: relative;
          overflow: hidden;
          border-radius: 40px;
          padding: clamp(24px, 5vw, 62px);
          border: 1px solid rgba(255,255,255,.76);
          background:
            radial-gradient(circle at 16% 18%, rgba(95,245,234,.25), transparent 32%),
            radial-gradient(circle at 84% 24%, rgba(255,224,138,.28), transparent 30%),
            linear-gradient(135deg, rgba(3,24,23,.96) 0%, rgba(7,49,47,.96) 52%, rgba(47,37,8,.96) 100%);
          box-shadow: 0 36px 100px rgba(0, 80, 74, .24);
          color: #fff;
        }

        .ard-hero::before {
          content: "";
          position: absolute;
          width: 360px;
          height: 360px;
          right: -130px;
          top: -120px;
          border-radius: 999px;
          background: radial-gradient(circle, rgba(95,245,234,.28), transparent 66%);
          pointer-events: none;
          animation: ardPulse 5s ease-in-out infinite;
        }

        .ard-hero::after {
          content: "";
          position: absolute;
          width: 520px;
          height: 48px;
          right: -80px;
          bottom: 58px;
          border-radius: 999px;
          background:
            linear-gradient(90deg, transparent, rgba(95,245,234,.22), rgba(255,224,138,.28), transparent);
          transform: rotate(-9deg);
          pointer-events: none;
          animation: ardBrush 5s ease-in-out infinite;
        }

        .ard-paintDrops {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
          opacity: .42;
        }

        .ard-drop {
          position: absolute;
          top: -30px;
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: ${BRAND.turquoiseLight};
          animation: ardFall 12s linear infinite;
        }

        .ard-drop:nth-child(1) { left: 10%; animation-delay: 0s; background: ${BRAND.turquoiseLight}; }
        .ard-drop:nth-child(2) { left: 24%; animation-delay: 2.2s; background: ${BRAND.goldLight}; width: 6px; height: 6px; }
        .ard-drop:nth-child(3) { left: 42%; animation-delay: 1s; background: ${BRAND.turquoise}; width: 10px; height: 10px; }
        .ard-drop:nth-child(4) { left: 66%; animation-delay: 3.6s; background: ${BRAND.gold}; }
        .ard-drop:nth-child(5) { left: 82%; animation-delay: 5.2s; background: ${BRAND.turquoiseLight}; width: 7px; height: 7px; }

        .ard-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: ${BRAND.turquoiseLight};
          font-weight: 950;
          letter-spacing: .15em;
          text-transform: uppercase;
          font-size: 11px;
          margin-bottom: 12px;
        }

        .ard-title {
          position: relative;
          z-index: 1;
          margin: 0;
          max-width: 960px;
          font-size: clamp(2.45rem, 6.5vw, 5.4rem);
          line-height: .92;
          letter-spacing: -.07em;
          color: #ffffff;
        }

        .ard-title span {
          color: ${BRAND.goldLight};
        }

        .ard-subtitle {
          position: relative;
          z-index: 1;
          max-width: 850px;
          margin: 18px 0 0;
          color: rgba(255,255,255,.78);
          line-height: 1.82;
          font-size: 1.08rem;
        }

        .ard-editorNote {
          position: relative;
          z-index: 1;
          max-width: 880px;
          margin-top: 20px;
          padding: 16px 18px;
          border-radius: 24px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.15);
          color: rgba(255,255,255,.78);
          line-height: 1.72;
          box-shadow: 0 18px 46px rgba(0,0,0,.18);
          backdrop-filter: blur(14px);
        }

        .ard-controls {
          position: relative;
          z-index: 1;
          margin-top: 26px;
          display: grid;
          grid-template-columns: 1fr auto auto auto;
          gap: 10px;
          max-width: 1010px;
        }

        .ard-input,
        .ard-btn {
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,.20);
          font: inherit;
          padding: 14px 16px;
        }

        .ard-input {
          min-width: 0;
          background: rgba(255,255,255,.92);
          color: ${BRAND.ink};
          box-shadow: inset 0 0 0 1px rgba(0,180,166,.10);
        }

        .ard-btn {
          cursor: pointer;
          font-weight: 950;
          color: #fff;
          background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
          box-shadow: 0 14px 30px rgba(0,180,166,.20);
          transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
          white-space: nowrap;
        }

        .ard-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 38px rgba(212,175,55,.24);
        }

        .ard-btn:disabled {
          opacity: .68;
          cursor: not-allowed;
          transform: none;
        }

        .ard-btn--ghost {
          background: rgba(255,255,255,.12);
          color: #fff;
          border-color: rgba(255,255,255,.22);
          box-shadow: none;
          backdrop-filter: blur(10px);
        }

        .ard-stats {
          position: relative;
          z-index: 1;
          display: flex;
          flex-wrap: wrap;
          gap: 9px;
          margin-top: 22px;
        }

        .ard-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.11);
          border: 1px solid rgba(255,255,255,.14);
          color: rgba(255,255,255,.84);
          font-size: 12px;
          font-weight: 850;
          backdrop-filter: blur(10px);
        }

        .ard-prompt {
          position: relative;
          z-index: 1;
          margin-top: 20px;
          display: inline-flex;
          max-width: 890px;
          padding: 13px 15px;
          border-radius: 20px;
          background: rgba(255,224,138,.15);
          border: 1px solid rgba(255,224,138,.20);
          color: rgba(255,255,255,.84);
          line-height: 1.68;
          font-weight: 750;
        }

        .ard-toolbar {
          margin-top: 24px;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 14px;
          align-items: center;
        }

        .ard-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ard-tab {
          border: 1px solid rgba(0,180,166,.15);
          background: rgba(255,255,255,.82);
          color: ${BRAND.turquoiseDark};
          border-radius: 999px;
          padding: 10px 13px;
          cursor: pointer;
          font-weight: 900;
          transition: transform .18s ease, box-shadow .18s ease;
        }

        .ard-tab:hover {
          transform: translateY(-1px);
        }

        .ard-tab--active {
          color: #fff;
          background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
          box-shadow: 0 12px 26px rgba(0,180,166,.18);
        }

        .ard-search {
          border: 1px solid rgba(0,180,166,.15);
          background: rgba(255,255,255,.88);
          color: ${BRAND.ink};
          border-radius: 999px;
          padding: 12px 15px;
          min-width: 300px;
          font: inherit;
          box-shadow: 0 14px 34px rgba(0,80,74,.07);
        }

        .ard-error,
        .ard-empty,
        .ard-loading {
          margin-top: 20px;
          border-radius: 30px;
          padding: 24px;
          background: rgba(255,255,255,.86);
          border: 1px solid rgba(0,180,166,.12);
          box-shadow: 0 18px 42px rgba(0,80,74,.09);
          backdrop-filter: blur(18px);
        }

        .ard-error {
          color: #8a2f2f;
          background: rgba(255,240,240,.92);
          border-color: rgba(210,80,80,.18);
        }

        .ard-loading {
          display: flex;
          gap: 18px;
          align-items: center;
        }

        .ard-loading h2,
        .ard-empty h2 {
          margin: 0;
          color: #064e46;
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          letter-spacing: -.04em;
        }

        .ard-loading p,
        .ard-empty p {
          margin: 7px 0 0;
          color: rgba(16,47,53,.68);
          line-height: 1.72;
        }

        .ard-empty--start {
          text-align: center;
          padding: 40px 24px;
        }

        .ard-loadingOrb {
          width: 74px;
          height: 74px;
          border-radius: 999px;
          background: radial-gradient(circle at 35% 32%, #fff7b0, ${BRAND.turquoise} 68%, ${BRAND.gold});
          box-shadow: 0 0 0 12px rgba(0,180,166,.08), 0 0 42px rgba(0,180,166,.28);
          flex-shrink: 0;
          animation: ardPulse 1.8s ease-in-out infinite;
        }

        .ard-section {
          margin-top: 34px;
        }

        .ard-sectionHead {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          align-items: end;
          margin-bottom: 16px;
        }

        .ard-sectionHead h2 {
          margin: 0;
          color: #064e46;
          font-size: clamp(1.72rem, 3vw, 2.75rem);
          letter-spacing: -.052em;
          line-height: 1;
        }

        .ard-sectionHead p {
          max-width: 850px;
          margin: 10px 0 0;
          color: rgba(16,47,53,.69);
          line-height: 1.74;
        }

        .ard-count {
          min-width: 54px;
          height: 54px;
          border-radius: 19px;
          display: inline-grid;
          place-items: center;
          color: #fff;
          font-weight: 950;
          background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
          box-shadow: 0 12px 26px rgba(0,180,166,.20);
        }

        .ard-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 18px;
        }

        .ard-grid--feature {
          grid-template-columns: 1.25fr 1fr 1fr;
        }

        .ard-card {
          min-width: 0;
          overflow: hidden;
          border-radius: 30px;
          background: rgba(255,255,255,.90);
          border: 1px solid rgba(0,180,166,.12);
          box-shadow: 0 18px 42px rgba(0,80,74,.10);
          transition: transform .18s ease, box-shadow .18s ease;
          backdrop-filter: blur(18px);
        }

        .ard-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 28px 62px rgba(0,80,74,.16);
        }

        .ard-card--feature {
          grid-row: span 2;
        }

        .ard-image {
          position: relative;
          height: 215px;
          overflow: hidden;
          background: linear-gradient(135deg, #0f766e, #99f6e4 55%, ${BRAND.gold});
        }

        .ard-image--feature {
          height: 360px;
        }

        .ard-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transform: scale(1.01);
          transition: transform .32s ease;
        }

        .ard-card:hover .ard-image img {
          transform: scale(1.055);
        }

        .ard-imageShade {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 36%, rgba(0,0,0,.34));
          pointer-events: none;
        }

        .ard-imageBadges {
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: 12px;
          z-index: 2;
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
        }

        .ard-imageBadges span {
          padding: 7px 10px;
          border-radius: 999px;
          background: rgba(0,0,0,.52);
          color: #fff;
          font-size: 11px;
          font-weight: 900;
          backdrop-filter: blur(8px);
        }

        .ard-image--fallback {
          display: grid;
          place-items: center;
          color: rgba(255,255,255,.94);
          font-weight: 950;
          letter-spacing: .08em;
          text-transform: uppercase;
          font-size: 12px;
        }

        .ard-fallbackOrb {
          position: absolute;
          border-radius: 999px;
          filter: blur(3px);
        }

        .ard-fallbackOrb--one {
          width: 150px;
          height: 150px;
          background: radial-gradient(circle, rgba(255,255,255,.72), transparent 70%);
          animation: ardFloat 6s ease-in-out infinite;
        }

        .ard-fallbackOrb--two {
          width: 90px;
          height: 90px;
          left: 12%;
          bottom: 12%;
          background: radial-gradient(circle, rgba(255,224,138,.70), transparent 70%);
          animation: ardFloat 7.5s ease-in-out infinite reverse;
        }

        .ard-cardBody {
          padding: 18px;
        }

        .ard-cardMeta {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-bottom: 11px;
        }

        .ard-cardMeta span,
        .ard-tag {
          display: inline-flex;
          padding: 6px 8px;
          border-radius: 999px;
          background: rgba(0,180,166,.09);
          color: ${BRAND.turquoiseDark};
          font-size: 11px;
          font-weight: 850;
        }

        .ard-card h3 {
          margin: 0;
          color: ${BRAND.ink};
          font-size: 1.24rem;
          line-height: 1.12;
          letter-spacing: -.028em;
        }

        .ard-venue {
          margin-top: 7px;
          color: ${BRAND.turquoiseDark};
          font-weight: 900;
          font-size: .95rem;
        }

        .ard-card p {
          margin: 10px 0 0;
          color: rgba(16,47,53,.70);
          line-height: 1.66;
        }

        .ard-curatorNote {
          margin-top: 12px;
          padding: 12px 13px;
          border-radius: 17px;
          background: rgba(0,180,166,.07);
          border: 1px solid rgba(0,180,166,.10);
          color: rgba(16,47,53,.72);
          line-height: 1.6;
          font-size: .92rem;
        }

        .ard-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 13px;
        }

        .ard-tag--gold {
          background: rgba(212,175,55,.14);
          color: #745500;
        }

        .ard-actions {
          display: flex;
          gap: 9px;
          flex-wrap: wrap;
          margin-top: 16px;
        }

        .ard-openBtn,
        .ard-mapBtn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 13px;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 950;
        }

        .ard-openBtn {
          background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
          color: #fff;
        }

        .ard-mapBtn {
          background: rgba(255,255,255,.82);
          border: 1px solid rgba(0,180,166,.18);
          color: ${BRAND.turquoiseDark};
        }

        .ard-footer {
          position: relative;
          z-index: 2;
          margin-top: 70px;
          color: #fff;
          background:
            radial-gradient(circle at 18% 20%, rgba(0,180,166,.22), transparent 30%),
            radial-gradient(circle at 82% 20%, rgba(212,175,55,.18), transparent 30%),
            linear-gradient(135deg, #061414 0%, #111b1a 58%, #171103 100%);
          border-radius: 34px 34px 0 0;
          border-top: 1px solid rgba(255,255,255,.16);
          box-shadow: 0 -20px 70px rgba(0,0,0,.12);
        }

        .ard-footerInner {
          width: min(1240px, calc(100% - 28px));
          margin: 0 auto;
          padding: 42px 0 28px;
          display: grid;
          grid-template-columns: 1.1fr 1.4fr;
          gap: 34px;
        }

        .ard-footerBrand {
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }

        .ard-footerLogo {
          width: 50px;
          height: 50px;
          border-radius: 16px;
          display: grid;
          place-items: center;
          flex-shrink: 0;
          color: #fff;
          font-weight: 950;
          background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
          box-shadow: 0 14px 34px rgba(0,180,166,.22);
        }

        .ard-footer h3,
        .ard-footer h4 {
          margin: 0 0 10px;
        }

        .ard-footer p {
          margin: 0;
          color: rgba(255,255,255,.70);
          line-height: 1.75;
          max-width: 520px;
        }

        .ard-footerCols {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 24px;
        }

        .ard-footerCols a,
        .ard-footerCols span {
          display: block;
          color: rgba(255,255,255,.70);
          text-decoration: none;
          margin: 8px 0;
          line-height: 1.45;
        }

        .ard-footerCols a:hover {
          color: ${BRAND.goldLight};
        }

        .ard-footerBottom {
          width: min(1240px, calc(100% - 28px));
          margin: 0 auto;
          padding: 18px 0 24px;
          display: flex;
          justify-content: space-between;
          gap: 14px;
          border-top: 1px solid rgba(255,255,255,.14);
          color: rgba(255,255,255,.58);
          font-size: 14px;
        }

        @keyframes ardPulse {
          0%, 100% { transform: scale(1); opacity: .78; }
          50% { transform: scale(1.06); opacity: 1; }
        }

        @keyframes ardFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-14px) rotate(1deg); }
        }

        @keyframes ardGridPulse {
          0%, 100% { opacity: .18; }
          50% { opacity: .36; }
        }

        @keyframes ardBrush {
          0%, 100% { transform: translateX(-20px) rotate(-9deg); opacity: .55; }
          50% { transform: translateX(22px) rotate(-7deg); opacity: .95; }
        }

        @keyframes ardFall {
          0% { transform: translateY(-20px); opacity: 0; }
          12% { opacity: .85; }
          100% { transform: translateY(640px); opacity: 0; }
        }

        @media (max-width: 1040px) {
          .ard-grid,
          .ard-grid--feature {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .ard-card--feature {
            grid-row: auto;
          }

          .ard-controls {
            grid-template-columns: 1fr 1fr;
          }

          .ard-toolbar {
            grid-template-columns: 1fr;
          }

          .ard-search {
            min-width: 0;
            width: 100%;
          }

          .ard-footerInner {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 720px) {
          .ard-shell {
            width: min(100% - 22px, 1240px);
            padding-top: 14px;
          }

          .ard-hero {
            border-radius: 28px;
            padding: 22px;
          }

          .ard-controls,
          .ard-grid,
          .ard-grid--feature,
          .ard-footerCols {
            grid-template-columns: 1fr;
          }

          .ard-sectionHead {
            align-items: start;
          }

          .ard-title {
            font-size: clamp(2.2rem, 13vw, 3.4rem);
          }

          .ard-image,
          .ard-image--feature {
            height: 245px;
          }

          .ard-loading {
            align-items: flex-start;
          }

          .ard-loadingOrb {
            width: 58px;
            height: 58px;
          }

          .ard-btn,
          .ard-input {
            width: 100%;
          }

          .ard-footerBottom {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="ard-shell">
        <section className="ard-hero">
          <div className="ard-paintDrops">
            <i className="ard-drop" />
            <i className="ard-drop" />
            <i className="ard-drop" />
            <i className="ard-drop" />
            <i className="ard-drop" />
          </div>

          <div className="ard-eyebrow">✦ Art For My Room Art Radar</div>

          <h1 className="ard-title">
            {heroTitle.includes("art") ? (
              heroTitle
            ) : (
              <>
                Find the best <span>art activities</span> around you
              </>
            )}
          </h1>

          <p className="ard-subtitle">{heroSubtitle}</p>

          <div className="ard-editorNote">{heroEditorNote}</div>

          <form className="ard-controls" onSubmit={submitCity}>
            <input
              className="ard-input"
              value={city}
              onChange={(event) => setCity(event.target.value)}
              placeholder="Enter London, Hackney, Peckham, Manchester..."
            />

            <button className="ard-btn" type="submit" disabled={isLoading}>
              {isLoading ? "Curating..." : "Load art radar"}
            </button>

            <button
              className="ard-btn ard-btn--ghost"
              type="button"
              onClick={useMyLocation}
              disabled={isLoading}
            >
              Use my location
            </button>

            <button
              className="ard-btn ard-btn--ghost"
              type="button"
              onClick={sharePage}
              disabled={!payload}
            >
              Share
            </button>
          </form>

          <div className="ard-stats">
            <span className="ard-pill">Location: {locName}</span>
            <span className="ard-pill">Best picks: {grouped.best.length}</span>
            <span className="ard-pill">Exhibitions: {grouped.exhibitions.length}</span>
            <span className="ard-pill">Art markets: {grouped.markets.length}</span>
            <span className="ard-pill">Workshops: {grouped.workshops.length}</span>
            <span className="ard-pill">Galleries: {grouped.galleries.length}</span>
            <span className="ard-pill">Museums: {grouped.museums.length}</span>
            <span className="ard-pill">Images: {counts.items_with_images || 0}</span>
            {payload?.meta?.ticketmaster_enabled ? (
              <span className="ard-pill">Ticketed events checked</span>
            ) : (
              <span className="ard-pill">Local sources checked</span>
            )}
          </div>

          <div className="ard-prompt">{painterPrompt}</div>
        </section>

        {isLoading ? <LoadingPanel /> : null}

        {error ? (
          <div className="ard-error">
            <strong>Could not load the local Art Radar.</strong>
            <div style={{ marginTop: 7 }}>{error}</div>
          </div>
        ) : null}

        {payload ? (
          <>
            <div className="ard-toolbar">
              <div className="ard-tabs">
                {[
                  ["best", "Best picks"],
                  ["exhibitions", "Exhibitions"],
                  ["markets", "Art markets"],
                  ["workshops", "Workshops"],
                  ["galleries", "Galleries"],
                  ["museums", "Museums"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`ard-tab ${
                      activeView === value ? "ard-tab--active" : ""
                    }`}
                    onClick={() => setActiveView(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <input
                className="ard-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search painting, market, fair, workshop..."
              />
            </div>

            <Section
              eyebrow={getReadableType(activeView)}
              title={
                activeView === "best"
                  ? "The best art leads first"
                  : activeView === "exhibitions"
                    ? "Temporary exhibitions and painting shows"
                    : activeView === "markets"
                      ? "Art markets, fairs and buying routes"
                      : activeView === "workshops"
                        ? "Workshops, talks, open studios and life drawing"
                        : activeView === "galleries"
                          ? "Gallery spaces and current shows"
                          : "Museums and collections worth opening"
              }
              text={
                activeView === "best"
                  ? "A mixed shelf of high-value art activities, ranked for painters, buyers, visual relevance, useful images, distance and discovery potential."
                  : activeView === "exhibitions"
                    ? "Temporary exhibitions, private views, openings and shows where artists can study presentation, colour, curation and audience response."
                    : activeView === "markets"
                      ? "Art markets, fairs, print fairs, makers markets and collector-friendly places where buying behaviour becomes visible."
                      : activeView === "workshops"
                        ? "Hands-on activities and artist-facing events, including talks, open studios, life drawing, painting classes and creative workshops."
                        : activeView === "galleries"
                          ? "Gallery and exhibition leads useful for seeing how artists present, describe, price and position their work."
                          : "Museums and collections useful for colour study, composition, references, art history and buyer inspiration."
              }
              items={filteredItems}
              emptyText="No matching results found. Try another search word, use Best picks, or load a wider city."
              featureFirst
            />

            <Section
              eyebrow="For painters"
              title="Study how art is shown"
              text="Useful leads for artists who want to understand presentation, descriptions, pricing signals, audience behaviour and how paintings sit in real cultural spaces."
              items={uniqueItems([
                ...grouped.exhibitions,
                ...grouped.galleries,
                ...grouped.museums,
                ...grouped.workshops,
              ])
                .sort(byScore)
                .slice(0, 6)}
              emptyText="No painter study leads found yet."
            />

            <Section
              eyebrow="For buyers"
              title="Find art worth seeing before buying"
              text="Useful stops for people looking for original paintings, art markets, gallery visits, exhibitions, room inspiration and visual confidence before choosing artwork."
              items={uniqueItems([
                ...grouped.markets,
                ...grouped.exhibitions,
                ...grouped.best,
              ])
                .sort(byScore)
                .slice(0, 6)}
              emptyText="No buyer-friendly art leads found yet."
            />
          </>
        ) : !isLoading ? (
          <EmptyStartPanel />
        ) : null}
      </div>

      <Footer />
    </main>
  );
}
